import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { SITE_CONFIG } from "../config/siteConfig";
import { useT } from "../i18n/i18n";
import SocialIconButton from "./SocialIconButton";

const ICON_CLASSNAME =
  "w-[16px] h-[16px] grayscale brightness-0 opacity-80 dark:invert";
const CHIP_CLASSNAME =
  "inline-flex items-center rounded-full bg-black/5 dark:bg-white/10 px-3 py-1 text-[12px] text-[rgb(var(--app-muted))]";

function ArrowRightIcon({ className = "" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M5 12h12M13 6l6 6-6 6"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

ArrowRightIcon.propTypes = { className: PropTypes.string };

function ChatBubbleIcon({ className = "" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

ChatBubbleIcon.propTypes = { className: PropTypes.string };

function ExternalLinkIcon({ className = "" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M14 5h5v5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10 14L19 5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M19 14v5H5V5h5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

ExternalLinkIcon.propTypes = { className: PropTypes.string };

function FeaturedCard({ title, description, tags, iconSrc, href }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="group relative rounded-2xl border border-black/10 dark:border-white/15 bg-[rgb(var(--app-card))] p-6 shadow-[0_1px_0_rgba(0,0,0,0.04)] hover:shadow-[0_12px_30px_rgba(0,0,0,0.08)] transition-shadow"
    >
      <span className="absolute right-5 top-5 text-[rgb(var(--app-muted))] group-hover:text-[rgb(var(--app-fg))] transition-colors">
        <ExternalLinkIcon className="h-5 w-5" />
      </span>
      <div className="flex gap-5">
        <div className="shrink-0">
          <img
            src={iconSrc}
            alt=""
            className="h-[74px] w-[74px] rounded-xl bg-black/5 dark:bg-white/5 p-2"
          />
        </div>
        <div className="min-w-0">
          <h3 className="font-bold text-[16px] sm:text-[17px] leading-snug text-[rgb(var(--app-fg))]">
            {title}
          </h3>
          <p className="mt-2 text-[14px] leading-relaxed text-[rgb(var(--app-muted))]">
            {description}
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {tags.map((tag) => (
              <span key={tag} className={CHIP_CLASSNAME}>
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>
    </a>
  );
}

FeaturedCard.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  tags: PropTypes.arrayOf(PropTypes.string).isRequired,
  iconSrc: PropTypes.string.isRequired,
  href: PropTypes.string.isRequired,
};

export default function Hero({ language }) {
  const { t, tArray } = useT(language);
  const headlineLines = tArray("hero.headlineLines");

  return (
    <section className="relative w-screen h-screen overflow-hidden flex items-center justify-center">
      {/* Full-screen video background */}
      <video
        autoPlay
        muted
        loop
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
        poster="/hero-page-image.jpg"
      >
        <source src="/portfolio-home-page.mp4" type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      {/* Dark overlay for text readability */}
      <div className="absolute inset-0 bg-black/40 dark:bg-black/50" />

      {/* Content overlay - centered */}
      <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 text-center">
        <h1 className="text-[32px] sm:text-[44px] lg:text-[56px] leading-[1.1] font-extrabold tracking-[-0.02em] text-white break-words">
          {Array.isArray(headlineLines) && headlineLines.length > 0 ? (
            headlineLines.map((line, idx) => (
              <span key={idx} className="block">
                {line}
              </span>
            ))
          ) : (
            <span>{t("hero.headline")}</span>
          )}
        </h1>

        <p className="mt-6 text-[14px] sm:text-[16px] lg:text-[18px] leading-relaxed text-white/90">
          {t("hero.subtitle")}
        </p>

        <div className="mt-10 flex flex-wrap gap-4 justify-center">
          <Link
            to="/portfolio"
            className="btn-outline inline-flex items-center gap-3 rounded-lg px-5 py-3 font-semibold text-sm sm:text-base border border-white"
          >
            {t("hero.ctaProjects")}
            <ArrowRightIcon className="h-5 w-5 flex-shrink-0" />
          </Link>
        </div>

        <div className="mt-16 sm:mt-20 flex items-center gap-3 flex-wrap justify-center">
          <SocialIconButton href={SITE_CONFIG.instagram} label="Instagram" icon="instagram" />
          <SocialIconButton href={SITE_CONFIG.linkedin} label="LinkedIn" icon="linkedin" />
          <SocialIconButton href={SITE_CONFIG.github} label="GitHub" icon="github" />
          <SocialIconButton href={`mailto:${SITE_CONFIG.email}`} label="Email" icon="email" />
        </div>
      </div>


    </section>
  );
}

Hero.propTypes = {
  language: PropTypes.oneOf(["en", "it"]),
};
